// CE Site Overview — entry point for the Compliance Evidence skill.
// Shows all registered CE sites with evidence counts and a quick-derive action.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { errorDetail } from '../services/api';

const LAYER_COLORS = {
  L1: 'bg-blue-900/60 text-blue-300 border-blue-700',
  L2: 'bg-emerald-900/60 text-emerald-300 border-emerald-700',
};

function StatBadge({ label, value, color = 'text-gray-200' }) {
  return (
    <div className="flex flex-col items-center">
      <span className={`text-lg font-bold font-mono ${color}`}>{value ?? '—'}</span>
      <span className="text-xs text-gray-500 mt-0.5">{label}</span>
    </div>
  );
}

// Dark-mode adaptation of DCFleet.jsx's StatusBadge — same healthy/degraded/
// offline naming as the backend's CollectorHealthStatus enum, swapped to the
// -900/60 / -300 / -700 variants the rest of this (dark-themed) page uses.
const HEALTH_BADGE_CONFIG = {
  healthy: { label: 'Healthy', bg: 'bg-emerald-900/60', text: 'text-emerald-300', border: 'border-emerald-700', dot: 'bg-emerald-500' },
  degraded: { label: 'Degraded', bg: 'bg-amber-900/60', text: 'text-amber-300', border: 'border-amber-700', dot: 'bg-amber-500' },
  offline: { label: 'Offline', bg: 'bg-red-900/60', text: 'text-red-300', border: 'border-red-700', dot: 'bg-red-500' },
  unknown: { label: 'Unknown', bg: 'bg-gray-700/60', text: 'text-gray-400', border: 'border-gray-600', dot: 'bg-gray-500' },
  // 2026-08-19 collector retirement: retired must look retired — muted and
  // struck-through-adjacent, never a live-health color. A retired collector is
  // historical identity, not active instrumentation.
  retired: { label: 'Retired', bg: 'bg-gray-800/80', text: 'text-gray-500', border: 'border-gray-700', dot: 'bg-gray-600' },
};

function HealthBadge({ status }) {
  const c = HEALTH_BADGE_CONFIG[status] || HEALTH_BADGE_CONFIG.unknown;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

function CollectorHealthRow({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-300 font-mono text-right">{value ?? '—'}</span>
    </div>
  );
}

// Front-page evidence a collector was alive during the reporting period —
// visible without clicking into a detail page, per the directive.
function CollectorHealthCard({ collectors }) {
  if (!collectors) {
    return <div className="py-2 text-center text-xs text-gray-500">Loading collector health...</div>;
  }
  if (collectors.length === 0) {
    return (
      <p className="text-xs text-gray-500">
        No collector assigned — evidence collection is not active for this site.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {collectors.map(c => (
        <div key={c.id} className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-200">{c.collector_name}</span>
            <HealthBadge status={c.health_status} />
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <CollectorHealthRow label="Agent version" value={c.agent_version} />
            <CollectorHealthRow label="Last evidence captured" value={c.last_seen_at ? new Date(c.last_seen_at).toLocaleString() : 'Never'} />
            <CollectorHealthRow label="Capture interface" value={c.capture_interface} />
            <CollectorHealthRow label="Signing status" value={c.signing_status} />
            <CollectorHealthRow label="Connectivity" value={c.connectivity_status} />
            <CollectorHealthRow label="Time sync" value={c.time_sync_status} />
            <CollectorHealthRow label="Queue depth" value={c.queue_depth} />
            <CollectorHealthRow label="Disk utilization" value={c.disk_utilization_pct} />
            <CollectorHealthRow label="Dropped evidence" value={c.dropped_evidence_count} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SiteCard({ site, summary, collectors, onDerive }) {
  const [showTools, setShowTools] = useState(false);
  const [deriving, setDeriving] = useState(false);
  const [deriveResult, setDeriveResult] = useState(null);

  const [deriveProgress, setDeriveProgress] = useState(null);

  /**
   * Derive the last 24 hours as 24 sequential one-hour requests.
   *
   * POST /ce/derive is synchronous and runs on the shared API process. Measured on real
   * Foster Rd data, a single 24-hour window holds 17,970 observed events, blocks for
   * 70-113 seconds, intermittently 500s, and repeated calls exhausted the process
   * outright. The endpoint now refuses windows above ~5,000 events (a measured 10-second
   * worst case) with a 413, so a single 24h call would simply be rejected at any busy site.
   *
   * Chunking here keeps the button's promise — the last 24 hours really are derived —
   * while every individual request stays inside the measured-safe envelope. An hour at
   * Foster's rate is roughly 750 events, about two seconds.
   *
   * Partial failure is reported as partial. If hour 14 fails, the first 13 hours were
   * genuinely derived and saying otherwise in either direction would be wrong: claiming
   * success hides a gap, and claiming total failure discards real work and invites a
   * pointless retry of everything.
   */
  const handleDerive = async () => {
    const HOURS = 24;
    setDeriving(true);
    setDeriveResult(null);
    setDeriveProgress({ done: 0, total: HOURS });

    const now = new Date();
    let created = 0;
    let reused = 0;
    let failedAt = null;
    let failMsg = null;

    for (let h = HOURS; h > 0; h -= 1) {
      const end = new Date(now.getTime() - (h - 1) * 3600 * 1000);
      const start = new Date(now.getTime() - h * 3600 * 1000);
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.post('/ce/derive', {
          site_id: site.id,
          window_start_at: start.toISOString(),
          window_end_at: end.toISOString(),
          rule_set_version: 'v1',
        });
        created += data.created_count || 0;
        reused += data.reused_count || 0;
        setDeriveProgress({ done: HOURS - h + 1, total: HOURS });
      } catch (err) {
        failedAt = HOURS - h;          // hours successfully completed before this one
        failMsg = errorDetail(err, 'Derivation failed');
        break;
      }
    }

    if (failedAt === null) {
      setDeriveResult({ ok: true, created, reused, hours: HOURS });
    } else {
      setDeriveResult({
        ok: false,
        partial: failedAt > 0,
        hours: failedAt,
        created,
        reused,
        msg: failMsg,
      });
    }
    setDeriveProgress(null);
    setDeriving(false);
    onDerive();
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-white">{site.site_name}</h3>
          <p className="text-xs text-gray-400 font-mono mt-0.5">{site.site_code}</p>
          {site.site_type && (
            <span className="inline-block mt-1 px-1.5 py-0.5 text-xs rounded bg-gray-700 text-gray-300">
              {site.site_type}
            </span>
          )}
        </div>
        <div className="flex gap-1.5">
          <span className="px-2 py-0.5 rounded text-xs border font-mono border-blue-700 text-blue-300">L1</span>
          <span className="px-2 py-0.5 rounded text-xs border font-mono border-emerald-700 text-emerald-300">L2</span>
        </div>
      </div>

      {summary ? (
        <div className="grid grid-cols-3 gap-3 py-3 border-t border-b border-gray-700">
          <StatBadge label="Observed" value={summary.observed_event_count} color="text-blue-300" />
          <StatBadge label="Derived" value={summary.derived_event_count} color="text-emerald-300" />
          <StatBadge label="Timelines" value={summary.timeline_count} />
          <StatBadge label="Open Inc." value={summary.open_incident_count}
            color={summary.open_incident_count > 0 ? 'text-amber-300' : 'text-gray-200'} />
          <StatBadge label="Bundles" value={summary.evidence_bundle_count} />
          {summary.stale_timeline_count > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-xs bg-amber-900/50 text-amber-300 border border-amber-700 px-1.5 py-0.5 rounded font-mono">
                {summary.stale_timeline_count} STALE
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="py-4 text-center text-xs text-gray-500">Loading summary...</div>
      )}

      <div className="border-t border-gray-700 pt-3">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Collector Health</h4>
        <CollectorHealthCard collectors={collectors} />
      </div>

      {/* One entry point, not five siblings.
          Before this the card offered Timelines, Incidents, Explore, Bundles and "Derive 24h" at
          identical visual weight, so nothing indicated which one a person actually wanted — the
          same flat-choice problem the nav had. Generating a packet is the reason this product
          exists, so it is the primary action; investigation tools collapse behind one disclosure;
          and "Derive 24h" is separated because it is not navigation at all — it MUTATES data by
          running derivation for the site, and it sat one careless click away from four harmless
          links. */}
      <div className="border-t border-gray-700 pt-3 space-y-2">
        <Link
          to={`/ce/evidence-packet?site=${site.id}`}
          data-testid={`site-primary-${site.id}`}
          className="block w-full text-center px-4 py-2 text-sm rounded-lg bg-purple-700 hover:bg-purple-600 text-white font-medium transition-colors"
        >
          Generate Packet
        </Link>

        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setShowTools((v) => !v)}
            data-testid={`site-investigate-${site.id}`}
            className="text-xs text-gray-400 hover:text-gray-200 transition-colors"
          >
            {showTools ? '▼' : '▶'} Investigate this site
          </button>
          <Link
            to={`/ce/bundles?site=${site.id}`}
            className="text-xs text-gray-400 hover:text-gray-200 underline"
          >
            Packets
          </Link>
        </div>

        {showTools && (
          <div className="flex flex-wrap gap-2 pt-1">
            <Link
              to={`/ce/explorer?site=${site.id}`}
              className="px-3 py-1.5 text-xs rounded bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
            >
              Search Evidence
            </Link>
            <Link
              to={`/ce/timelines?site=${site.id}`}
              className="px-3 py-1.5 text-xs rounded bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
            >
              Timelines
            </Link>
            <Link
              to={`/ce/incidents?site=${site.id}`}
              className="px-3 py-1.5 text-xs rounded bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
            >
              Incidents
            </Link>
          </div>
        )}

        {/* Deliberately last, visually quietest, and labelled as the data-changing action it is. */}
        <div className="pt-1">
          <button
            onClick={handleDerive}
            disabled={deriving}
            data-testid={`site-derive-${site.id}`}
            className="px-3 py-1 text-xs rounded border border-gray-600 text-gray-400 hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            {deriving ? 'Running…' : 'Run derivation (last 24h)'}
          </button>
        </div>
      </div>

      {deriveProgress && (
        <p data-testid={`derive-progress-${site.id}`} className="text-xs font-mono text-gray-400">
          Deriving hour {deriveProgress.done + 1} of {deriveProgress.total}…
        </p>
      )}

      {deriveResult && (
        <p
          data-testid={`derive-result-${site.id}`}
          className={`text-xs font-mono ${deriveResult.ok ? 'text-emerald-400' : deriveResult.partial ? 'text-amber-400' : 'text-red-400'}`}
        >
          {deriveResult.ok && (
            `Done — ${deriveResult.hours}h derived, ${deriveResult.created} created, ${deriveResult.reused} reused`
          )}
          {/* Partial is its own state, not a success and not a total failure. */}
          {!deriveResult.ok && deriveResult.partial && (
            `Stopped after ${deriveResult.hours} of 24 hours — ${deriveResult.created} created, `
            + `${deriveResult.reused} reused. The remaining hours were not derived. ${deriveResult.msg}`
          )}
          {!deriveResult.ok && !deriveResult.partial && deriveResult.msg}
        </p>
      )}
    </div>
  );
}

export default function CESiteOverview() {
  const [sites, setSites] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [collectorsBySite, setCollectorsBySite] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSites = useCallback(async () => {
    try {
      const { data } = await api.get('/ce/sites');
      setSites(data.sites || []);
      return data.sites || [];
    } catch (err) {
      setError(errorDetail(err, 'Failed to load sites'));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSummaries = useCallback(async (siteList) => {
    const results = await Promise.allSettled(
      siteList.map(s => api.get(`/ce/sites/${s.id}/summary`))
    );
    const map = {};
    siteList.forEach((s, i) => {
      if (results[i].status === 'fulfilled') map[s.id] = results[i].value.data;
    });
    setSummaries(map);
  }, []);

  const loadCollectors = useCallback(async (siteList) => {
    const results = await Promise.allSettled(
      siteList.map(s => api.get(`/ce/sites/${s.id}/collectors`))
    );
    const map = {};
    siteList.forEach((s, i) => {
      // Empty array (not the whole key missing) on failure too — an
      // errored fetch shouldn't be visually indistinguishable from "still
      // loading" forever; CollectorHealthCard treats undefined as loading
      // and [] as the real "no collector" empty state.
      map[s.id] = results[i].status === 'fulfilled' ? (results[i].value.data.collectors || []) : [];
    });
    setCollectorsBySite(map);
  }, []);

  useEffect(() => {
    loadSites().then(s => {
      loadSummaries(s);
      loadCollectors(s);
    });
  }, [loadSites, loadSummaries, loadCollectors]);

  const refresh = useCallback(() => {
    loadSites().then(s => {
      loadSummaries(s);
      loadCollectors(s);
    });
  }, [loadSites, loadSummaries, loadCollectors]);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Compliance Evidence</h1>
            <p className="text-sm text-gray-400 mt-1">
              Deterministic evidence factory — Layer 1 observed, Layer 2 derived, Layer 3 AI
            </p>
          </div>
          <button
            onClick={refresh}
            className="px-4 py-2 text-sm rounded bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
          >
            Refresh
          </button>
        </div>

        {loading && (
          <div className="text-center py-16 text-gray-400">Loading CE sites...</div>
        )}

        {error && (
          <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {!loading && !error && sites.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            No CE sites registered. Ingest an observed event to auto-register a site, or call
            <code className="ml-1 font-mono text-xs bg-gray-800 px-1 rounded">POST /api/ce/sites</code>.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sites.map(site => (
            <SiteCard
              key={site.id}
              site={site}
              summary={summaries[site.id]}
              collectors={collectorsBySite[site.id]}
              onDerive={refresh}
            />
          ))}
        </div>

        {!loading && sites.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/ce/explorer"
              className="px-4 py-2 text-sm rounded-lg bg-blue-800 hover:bg-blue-700 text-blue-100 transition-colors"
            >
              Evidence Explorer
            </Link>
            <Link
              to="/ce/timelines"
              className="px-4 py-2 text-sm rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
            >
              All Timelines
            </Link>
            <Link
              to="/ce/incidents"
              className="px-4 py-2 text-sm rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
            >
              All Incidents
            </Link>
            <Link
              to="/ce/bundles"
              className="px-4 py-2 text-sm rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
            >
              My Packets
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
